import { FLAGGED_REASON } from '@/shared/config';
import { generateIssueFound } from '@/entities/study';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import type { SolutionsDrawerProps } from './drawers.types';

/**
 * Derives the troubleshooting text shown in the studies list solutions drawer. On every change of the study it picks the pending-upload flagged reason when the study carries it and otherwise artifact.lastIssueFound, resolves that text with generateIssueFound against the device model, and stores the issue found, its description and the steps to try.
 *
 * @param data - Study whose flagged reason, artifact and device model drive the solution text.
 * @returns State holding the issue found, its description and the steps to try.
 */
export const useSolutionsDrawer = ({ data }: Pick<SolutionsDrawerProps, 'data'>) => {
  const [state, setState] = useMergeState<{
    issueFound: string;
    description: string;
    stepsToTry: ReturnType<typeof generateIssueFound>['stepsToTry'];
  }>({
    issueFound: '',
    description: '',
    stepsToTry: [],
  });

  useUpdateEffect(() => {
    const { flaggedStudy } = data || {};
    const text =
      flaggedStudy?.reason === FLAGGED_REASON.PENDING_UPLOAD
        ? FLAGGED_REASON.PENDING_UPLOAD
        : data?.artifact?.lastIssueFound;
    const { issueFound, description, stepsToTry } = generateIssueFound(text, data?.device?.model);
    setState({
      issueFound,
      description,
      stepsToTry,
    });
  }, [data]);

  return { state };
};
